import appConfig from '../../config/appConfig';
import {ENABLE_USAGE_HISTORY_DISPLAY} from '../../consts/tnc/aggregatedSettingTypes';
import * as usageDateTypes from '../../consts/uam/usageDateTypes';
import {clearUsageStatsDetails, setAssetIdList} from '../../state/ducks/usageStatistic';
import {validateYapLastDataCollectionTime} from '../../state/ducks/yapEncrypted';
import {
    makeSelectConsumerProductsIds,
    makeSelectConsumerProductsWithWrongCodentifyId,
    makeSelectIotDeviceMergedWithConsumerProduct,
    makeSelectIsIotProductRegistered,
    makeSelectLegalBySettingsCode,
} from '../../state/selectors/consumer';
import {selectIotDeviceData, selectIsMonoDevice} from '../../state/selectors/iotDevice';
import {selectAssetsIdList} from '../../state/selectors/usageStatistic';
import {makeSelectAssetIdByType} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import deviceTypesService from '../device/deviceTypesService';
import * as gigyaService from '../gigya/gigyaService';
import iccConsumerProducts from '../icc/iccConsumerProducts';
import iccMarketService from '../icc/iccMarketService';
import iccService from '../icc/iccService';
import marketTypeService from '../marketTypeService';
import muleSoftMarketService from '../muleSoft/muleSoftMarketService';
import uamClientService from './uamClientService';
import usageStatsConverter from './usageStatsConverter';

const USAGE_DATA_DAYS_COUNT_DATA = 89; //89 + 1 (today) = 90
const USAGE_DATA_6HOURS_KEY = '6HOURS';

const isUsageStatsEnable = (type) => {
    const isArianeDevice = deviceTypesService.isAriane(type);
    const isGlobalUsageAvailableAriane = appConfig.getIsUsageAvailableAriane();
    const isUsageAvailableAriane = isGlobalUsageAvailableAriane && isArianeDevice;
    const isUsageAvailableP1 = appConfig.getIsUsageAvailableP1() && deviceTypesService.isP1(type);
    const isUsageAvailableP4 = appConfig.getIsUsageAvailableP4() && deviceTypesService.isP4(type);
    const isUsageAvailableP1V4 = appConfig.getIsUsageAvailableP1V4() && deviceTypesService.isP1V4(type);

    return (
        (isUsageAvailableP1 && (isArianeDevice ? isGlobalUsageAvailableAriane : true)) ||
        isUsageAvailableP4 ||
        isUsageAvailableP1V4 ||
        isUsageAvailableAriane
    );
};

const initUsageData = async (deviceType) => {
    dispatch(clearUsageStatsDetails());
    const isSuccess = await refreshUsageData();

    if (!isSuccess) throw new Error('Usage data is not retrieved for current device');

    const deviceTypeFilterFunction = getDeviceTypeFilterFunction(deviceType);

    if (!deviceTypeFilterFunction) return;

    await updateUserDevices(deviceTypeFilterFunction);

    const isAriane = deviceTypesService.isAriane(deviceType);
    const isP1 = deviceTypesService.isP1(deviceType);
    const isP1V4 = deviceTypesService.isP1V4(deviceType);
    const isP4 = deviceTypesService.isP4(deviceType);
    const range = getDateRange();
    const data = getDataForUsageByRange(range, isP1V4);

    const state = getState();
    const consumerProducts = makeSelectConsumerProductsIds(deviceTypeFilterFunction)(state);
    const usageStats = await getUsageData({
        productList: consumerProducts,
        data,
        isAriane,
        isP1: isP1 && !isAriane,
        isP1V4,
        isP4,
    });

    usageStatsConverter.saveUsageStats(usageStats, range);
};

const updateUserDevices = async (deviceTypeFilterFunction) => {
    const isDCE2 = marketTypeService.isDCE2();

    if (isDCE2) {
        await muleSoftMarketService.getConsumerAssets();
    }

    const gigyaToken = gigyaService.getGigyaToken(true);

    await iccMarketService.getConsumerProducts(gigyaToken);

    if (isDCE2) return;

    const consumerProductsInfoList =
        makeSelectConsumerProductsWithWrongCodentifyId(deviceTypeFilterFunction)(getState()) || [];
    const serialNumbers = consumerProductsInfoList.map(({deviceSerialNumber}) => deviceSerialNumber).filter((id) => id);

    await iccConsumerProducts.getDetailedProductsInfoBySerialNumbers(serialNumbers);
};

const getDeviceTypeFilterFunction = (deviceType) => {
    const isAriane = deviceTypesService.isAriane(deviceType);
    const isP1 = deviceTypesService.isP1(deviceType);
    const isP1V4 = deviceTypesService.isP1V4(deviceType);
    const isP4 = deviceTypesService.isP4(deviceType);

    const isP1Available = appConfig.getIsUsageAvailableP1();
    const isArianeAvailable = appConfig.getIsUsageAvailableAriane();
    const isP1V4Available = appConfig.getIsUsageAvailableP1V4();
    const isP4Available = appConfig.getIsUsageAvailableP4();

    if (
        (isAriane && !isArianeAvailable) ||
        (isP1 && !isP1Available) ||
        (isP1V4 && !isP1V4Available) ||
        (isP4 && !isP4Available)
    )
        return;

    if (isAriane) return deviceTypesService.isAriane;

    if (isP1) return deviceTypesService.isP1ChargerWithoutAriane;

    if (isP1V4) return deviceTypesService.isP1V4HolderOrMono;

    if (isP4) return deviceTypesService.isP4;
};

const getUsageData = async ({productList, data, isAriane = false, isP1 = false, isP4 = false, isP1V4 = false}) => {
    if (!productList.length) return [];

    const usageData = await fetchUsageData({productList, data, isAriane, isP1, isP4, isP1V4});

    return usageData ? usageData : [];
};

const getDateRange = () => {
    const today = new Date();

    const from = usageStatsConverter.getDate(today, true, USAGE_DATA_DAYS_COUNT_DATA);
    const to = usageStatsConverter.getDate(today);

    return {to, from};
};

const getDataForUsageByRange = (range, isP1V4) => {
    const usageLegal = makeSelectLegalBySettingsCode([ENABLE_USAGE_HISTORY_DISPLAY])(getState());

    if (!usageLegal) throw new Error(`${ENABLE_USAGE_HISTORY_DISPLAY} legal is not found`);

    const dateFrom = new Date(range.from);

    //adapt summer and winter time
    dateFrom.setMinutes(dateFrom.getMinutes() + new Date().getTimezoneOffset() - dateFrom.getTimezoneOffset());

    const groupFrom = dateFrom.toISOString();
    const from = new Date(usageLegal.updateTs) < dateFrom ? groupFrom : usageLegal.updateTs;

    return {
        params: {
            from,
            groupFrom,
            to: range.to.toISOString(),
            maximumTimeDeltaInS: appConfig.getMaximumTimeDeltaInS(),
            splitByProfile: isP1V4,
        },
        args: [USAGE_DATA_6HOURS_KEY],
    };
};

const fetchUsageData = async ({productList, data, isAriane, isP1, isP4, isP1V4}) => {
    const {device, holder} = makeSelectIotDeviceMergedWithConsumerProduct()(getState());
    const {codentifyOriginal: chargerCodentify} = device || {};
    const {codentifyOriginal: holderCodentify} = holder || {};
    const assetsList = selectAssetsIdList(getState());
    const usageData = [];

    await Promise.all(
        productList.map(async (product) => {
            const {codentifyID} = product;
            const isUpdateUsageDataNeeded =
                codentifyID && codentifyID !== holderCodentify && codentifyID !== chargerCodentify;

            if (!isUpdateUsageDataNeeded) return;

            const assetIdData = assetsList?.find((assetItem) => assetItem.codentify === codentifyID);

            let {assetId, uamAuthToken, uamApiKey} = assetIdData || {};

            if (!assetIdData) {
                const uamTokenResponse = await iccService.fetchUamToken({codentify: codentifyID});

                if (!uamTokenResponse) throw new Error('Asset token is not found');

                uamAuthToken = uamTokenResponse.AuthToken;
                uamApiKey = uamTokenResponse.UamApiKey;

                const uamAssetResponse = await uamClientService.getAssetsByToken({
                    uamAuthToken,
                    uamApiKey,
                });

                if (!uamAssetResponse) throw new Error('Asset id is not found');

                const {assetId: newAssetId, device} = uamAssetResponse[0] || {};

                if (!newAssetId || device?.status === 'Device not found') return;

                assetId = newAssetId;

                dispatch(setAssetIdList({codentify: codentifyID, assetId, uamAuthToken, uamApiKey}));
            }

            const usageInfo = await uamClientService.getUamDevicesExperienceData({
                assetId,
                headerData: {
                    authToken: uamAuthToken,
                    apiKey: uamApiKey,
                },
                data,
                codentify: codentifyID,
            });

            if (!usageInfo) throw new Error('Usage data can not be retrieved');
            usageData.push({assetId, isAriane, isP1, isP4, isP1V4, ...usageInfo});
        })
    ).catch((e) => {
        dispatch(clearUsageStatsDetails());
        throw e;
    });

    return usageData;
};

const refreshUsageData = async () => {
    const state = getState();
    const isMonoDevice = selectIsMonoDevice(state);
    const iotDevice = selectIotDeviceData(state);
    const deviceType = iotDevice?.device?.type;
    const isAriane = deviceTypesService.isAriane(deviceType);
    const isP1 = deviceTypesService.isP1(deviceType);
    const isP4 = deviceTypesService.isP4(deviceType);
    const isP1V4 = deviceTypesService.isP1V4(deviceType);
    const range = getDateRange();
    const data = getDataForUsageByRange(range, isP1V4);
    const isTargetHolder = isP1V4 && !isMonoDevice;
    const isRegistered = makeSelectIsIotProductRegistered(isTargetHolder)(state);

    //don't stop data retrieving if current device is not registered, just skip it
    if (!isRegistered) return true;

    //update data as holder for isP1V4 only (not mono), other - charger
    const assetId = makeSelectAssetIdByType(isTargetHolder)(state);

    //the data will be retrieved in scope of all registered devices if null
    if (!assetId) return true;

    const usageInfo = await uamClientService.getUamDevicesExperienceData({
        assetId,
        data,
    });

    if (!usageInfo) return false;

    dispatch(validateYapLastDataCollectionTime());
    const usageData = {assetId, isAriane, isP1, isP4, isP1V4, ...usageInfo};

    usageStatsConverter.saveUsageStats([usageData], range);
    return true;
};

const getIsNbw2UsageEnabled = () => appConfig.getIsNbw2UsageEnabled();

const getIsHourTheFuture = ({item, targetRecord, dataIndex}) => {
    const currentDate = new Date();
    const currentHour = currentDate.getHours();
    const dayDate = targetRecord[dataIndex]?.date;
    const day = new Date(dayDate);
    const currentDay = new Date(currentDate).setHours(0, 0, 0, 0);

    if (day < currentDay) return;

    switch (item?.key) {
        case usageDateTypes.RANGE_0_6:
            return currentHour < 0;
        case usageDateTypes.RANGE_6_12:
            return currentHour < 6;
        case usageDateTypes.RANGE_12_18:
            return currentHour < 12;
        case usageDateTypes.RANGE_18_24:
            return currentHour < 18;
        default:
            return;
    }
};

export default {
    isUsageStatsEnable,
    initUsageData,
    refreshUsageData,
    getIsNbw2UsageEnabled,
    getIsHourTheFuture,
};
